import {
  CheckCircleIcon,
  ClockIcon,
  FileIcon,
  ShieldWarningIcon,
  SpinnerGapIcon,
  WarningCircleIcon
} from '@phosphor-icons/react';
import { BatchFileState } from '../types';
import { isFailed, statusLabel } from '../utils';
import FileRow from './FileRow';

interface BatchProgressProps {
  batchFiles: BatchFileState[];
  phase: 'uploading' | 'tracking';
}

function StatusIcon({ status, progress }: { status: string; progress: number }) {
  if (status === 'ready') {
    return <CheckCircleIcon size={16} weight="fill" className="text-green-500" />;
  }
  if (status === 'pending_upload') {
    return progress > 0 ? (
      <SpinnerGapIcon size={16} className="animate-spin text-content-default" />
    ) : (
      <ClockIcon size={16} className="text-content-subtle" />
    );
  }
  if (['uploaded', 'security_scan_pending', 'security_scanning', 'clean'].includes(status)) {
    return <SpinnerGapIcon size={16} className="animate-spin text-content-default" />;
  }
  if (status === 'infected') {
    return <ShieldWarningIcon size={16} weight="fill" className="text-red-600" />;
  }
  if (isFailed(status)) {
    return <WarningCircleIcon size={16} weight="fill" className="text-misc-error" />;
  }
  return <FileIcon size={16} className="text-content-subtle" />;
}

export default function BatchProgress({ batchFiles }: BatchProgressProps) {
  return (
    <div className="w-full overflow-y-auto custom-scrollbar max-h-64">
      {batchFiles.map((f) => (
        <FileRow
          key={f.fileId}
          name={f.name}
          size={f.size}
          iconNode={<StatusIcon status={f.status} progress={f.uploadProgress} />}
          subtitle={
            f.uploadProgress < 100 && f.status === 'pending_upload' ? (
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                  <div
                    className="h-full bg-blue-500 transition-all duration-300"
                    style={{ width: `${f.uploadProgress}%` }}
                  />
                </div>
                <span className="shrink-0">{f.uploadProgress}%</span>
              </div>
            ) : f.errorMessage ? (
              <span className="text-misc-error">{f.errorMessage}</span>
            ) : (
              statusLabel(f.status)
            )
          }
        />
      ))}
    </div>
  );
}
